'use client';

import { c as _c } from "react/compiler-runtime";
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import useProducts from '@shared/utils/hooks/useProducts';
import useUser from '@shared/utils/hooks/useUser';
import { Apple, BarChart3, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Clock, Eye, FileText, Globe, Handshake, Heart, Hourglass, LinkIcon, Lock, MapPin, Monitor, Play, PlayCircle, ShieldCheck, Sparkles, Tag, Target, Users } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useContext, useRef, useState } from 'react';
import { client, type RouterOutputs } from '../../utils/trpc';
import Button from '../common/Button';
import ProductImage from '../products/ProductImage';
import { PartnersCarousel } from '../UI/PartnersCarousel';
import AboutHeader from './AboutHeader';
import { faqs } from './faqData';
import MarketingFooter from './MarketingFooter';
import ScrollAnimations from './ScrollAnimations';

// Lazy-load below-fold sections to shrink the landing-page bundle.
// FeatureRequests fetches via React Query (no SSR data to preserve).
const FeatureRequests = dynamic(() => import('./FeatureRequests').then(mod => mod.FeatureRequests), {
  ssr: false
});
// Modals: only rendered after interaction — defer the bundle.
// PurchaseModal pulls @stripe/react-stripe-js + stripe-js/pure. AppWidgets
// already defers it for exactly that reason; importing it statically here put
// Stripe straight back onto the anonymous landing page it was removed from.
const PurchaseModal = dynamic(() => import('../common/PurchaseModal'), {
  ssr: false
});
const ContactFormModal = dynamic(() => import('./ContactFormModal'), {
  ssr: false
});
const DesktopDownloadModal = dynamic(() => import('./DesktopDownloadModal'), {
  ssr: false
});
interface Feature {
  id: string;
  title: string;
  description: string;
  videoSrc: string;
  backgroundImage?: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const features: Feature[] = [{
  id: '1',
  title: 'Map-Based Post Filtering',
  description: 'Filter posts by location with precise geospatial data. Find relevant information based on where you are or where events happen.',
  videoSrc: '/about/map.webp',
  backgroundImage: '/about/map.webp',
  icon: MapPin
}, {
  id: '2',
  title: 'Versatile Tagging System',
  description: 'Pair all forms of media to make factiis. Our "For Sale" factiii example shows how you can combine media, location, and data requirements.',
  videoSrc: '/about/factiiis.webp',
  backgroundImage: '/about/factiiis.webp',
  icon: Tag
}, {
  id: '3',
  title: 'Usage Analysis',
  description: 'Optional local engagement tracker using local database that keeps your analytics entirely on your device.',
  videoSrc: '/about/analytics.webp',
  backgroundImage: '/about/analytics.webp',
  icon: BarChart3
}, {
  id: '4',
  title: 'Private Posts',
  description: 'Posts that stay entirely on your end. Can be used to collect data, notes, and personal information without any server involvement.',
  videoSrc: '/about/private_posts.webp',
  backgroundImage: '/about/private_posts.webp',
  icon: Lock
}, {
  id: '5',
  title: 'Encrypted Vault',
  description: 'Store your 2FA codes, database backups, and imported authenticator keys — all encrypted on your device. Sync across devices with cloud backup or transfer via QR code.',
  videoSrc: '/about/vault.webp',
  backgroundImage: '/about/vault.webp',
  icon: ShieldCheck
}];

// Benefits data
interface Benefit {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const benefits: Benefit[] = [{
  id: '1',
  title: 'Verify Information',
  description: 'Source your claims with citations and collaborate with the community to fact-check content.',
  icon: CheckCircle
}, {
  id: '2',
  title: 'Unique Tagging',
  description: 'Build custom factiiis to label posts in 1000+ ways and make content more meaningful.',
  icon: Tag
}, {
  id: '3',
  title: 'Topic-Based Spaces',
  description: 'Join or create spaces for your interests and collaborate with like-minded people.',
  icon: Users
}, {
  id: '4',
  title: 'Privacy-First',
  description: 'Your data stays on your device. You control what to share and what stays private.',
  icon: ShieldCheck
}, {
  id: '5',
  title: 'Map-Based Posts',
  description: 'Filter posts by location with precise geospatial data for relevant local information.',
  icon: MapPin
}, {
  id: '6',
  title: 'Usage Analytics',
  description: 'Optional local analytics that track your engagement and never leave your device.',
  icon: BarChart3
}];

// Team vision data
interface TeamVision {
  id: string;
  name: string;
  role: string;
  content: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const teamVisions: TeamVision[] = [{
  id: '1',
  name: 'On Truth',
  role: 'Our Mission',
  content: 'We believe everyone deserves access to verified, unbiased information. Factiii exists to make truth easy to find and impossible to bury.',
  icon: Eye
}, {
  id: '2',
  name: 'On Privacy',
  role: 'Our Promise',
  content: 'Your data is yours. We built Factiii local-first because we believe privacy is a right, not a feature you pay for.',
  icon: ShieldCheck
}, {
  id: '3',
  name: 'On Community',
  role: 'Our Vision',
  content: 'The best fact-checking happens when people work together. We are building tools that empower communities to hold information accountable.',
  icon: Heart
}];

// FAQ data lives in faqData.ts so the server-rendered FAQPage JSON-LD on the
// homepage stays in sync with this accordion.

// Feature image with skeleton shimmer and fade-in on load
const FeatureImage = t0 => {
  const $ = _c(12);
  const {
    src,
    alt
  } = t0;
  const [loaded, setLoaded] = useState(false);
  let t1;
  if ($[0] !== loaded) {
    t1 = !loaded && <div className="absolute inset-0 overflow-hidden bg-slate-200/60 dark:bg-slate-700/40"><div className="absolute inset-0 animate-shimmer-sweep bg-gradient-to-r from-transparent via-white/50 to-transparent" /></div>;
    $[0] = loaded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `relative size-full transition-opacity duration-300 ease-out ${loaded ? "opacity-100" : "opacity-0"}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setLoaded(true);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== alt || $[4] !== src) {
    t4 = <Image src={src} alt={alt} fill={true} loading="lazy" className="object-cover" onLoad={t3} />;
    $[3] = alt;
    $[4] = src;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <div className="relative aspect-video">{t1}{t5}</div>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
type AboutPageProps = {
  statsData?: RouterOutputs['stats']['aboutStats'] | null;
  // Server-rendered "Explore" hub (links into factiiis/spaces/posts), passed as
  // a slot so its SSR HTML stays crawlable even though About is a client page.
  exploreSlot?: React.ReactNode;
  // Company/E-E-A-T section (CompanySection), passed only by /about so that
  // page carries unique content beyond the logged-out marketing homepage at /.
  // Rendered right under the hero, so the trust content leads that page.
  companySlot?: React.ReactNode;
  // false = the company page: hero, company section, values, mission and the
  // closing CTA only. The partner strip, feature tour, pricing and FAQ stay on
  // the homepage, so /about no longer repeats ~90% of it (duplicate content,
  // SEO audit 2026-09-29).
  marketing?: boolean;
  // /about's own H1 and lead. Without it the hero is the homepage tagline,
  // and the two pages shared an H1.
  hero?: {
    title: React.ReactNode;
    lead: React.ReactNode;
  };
};
export const AboutPage = t0 => {
  const $ = _c(110);
  const {
    statsData,
    exploreSlot,
    companySlot,
    marketing: t1,
    hero
  } = t0;
  const marketing = t1 === undefined ? true : t1;
  const {
    currentUser
  } = useUser();
  const {
    products
  } = useProducts(client);
  const router = useRouter();
  const {
    openSignupModal,
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const [desktopDownloadOpen, setDesktopDownloadOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [foundersModalOpen, setFoundersModalOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const stats = {
    users: statsData?.userCount || 0,
    factiiis: statsData?.postFactiiiCount || 0,
    posts: statsData?.postCount || 0,
    partners: 3
  };
  let foundersToken;
  let t2;
  if ($[0] !== products) {
    const tronProducts = products.filter(_temp);
    foundersToken = products.find(_temp2);
    t2 = tronProducts.filter(_temp3).sort(_temp4);
    $[0] = products;
    $[1] = foundersToken;
    $[2] = t2;
  } else {
    foundersToken = $[1];
    t2 = $[2];
  }
  const purchasableTrons = t2;
  const leftTron = purchasableTrons[0];
  const rightTron = purchasableTrons.length > 1 ? purchasableTrons[purchasableTrons.length - 1] : undefined;
  let goToNext;
  let goToPrevious;
  let scrollToIndex;
  let t3;
  if ($[3] !== currentIndex) {
    goToNext = () => {
      const nextIndex = (currentIndex + 1) % features.length;
      setCurrentIndex(nextIndex);
      scrollToIndex(nextIndex);
    };
    goToPrevious = () => {
      const prevIndex = currentIndex === 0 ? features.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIndex);
      scrollToIndex(prevIndex);
    };
    scrollToIndex = index => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const children = Array.from(container.querySelectorAll("[data-feature-card=\"true\"]"));
      const target = children[index];
      if (!target) {
        return;
      }
      const targetCenter = (target as HTMLElement).offsetLeft + (target as HTMLElement).offsetWidth / 2;
      const containerCenter = container.offsetWidth / 2;
      container.scrollTo({
        left: Math.max(0, targetCenter - containerCenter),
        behavior: "smooth"
      });
    };
    t3 = () => {
      const container_0 = scrollContainerRef.current;
      if (!container_0) {
        return;
      }
      const containerCenter_0 = container_0.scrollLeft + container_0.offsetWidth / 2;
      const children_0 = Array.from(container_0.querySelectorAll("[data-feature-card=\"true\"]"));
      if (children_0.length === 0) {
        return;
      }
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      for (let i = 0; i < children_0.length; i++) {
        const el = children_0[i];
        const elCenter = (el as HTMLElement).offsetLeft + (el as HTMLElement).offsetWidth / 2;
        const distance = Math.abs(elCenter - containerCenter_0);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }
      if (closestIndex !== currentIndex) {
        setCurrentIndex(closestIndex);
      }
    };
    $[3] = currentIndex;
    $[4] = goToNext;
    $[5] = goToPrevious;
    $[6] = scrollToIndex;
    $[7] = t3;
  } else {
    goToNext = $[4];
    goToPrevious = $[5];
    scrollToIndex = $[6];
    t3 = $[7];
  }
  const handleManualScroll = t3;
  let t4;
  if ($[8] !== scrollToIndex) {
    t4 = () => {
      const id = requestAnimationFrame(() => {
        scrollToIndex(0);
      });
      return () => cancelAnimationFrame(id);
    };
    $[8] = scrollToIndex;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[11] !== currentIndex || $[12] !== scrollToIndex) {
    t6 = () => {
      const onResize = () => {
        scrollToIndex(currentIndex);
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    };
    $[11] = currentIndex;
    $[12] = scrollToIndex;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== currentIndex) {
    t7 = [currentIndex];
    $[14] = currentIndex;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  React.useEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ScrollAnimations />;
    t9 = <AboutHeader />;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = statsData && <div className="mb-6 flex items-center gap-3"><div className="flex -space-x-2"><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /><div className="size-6 rounded-full border-2 border-background-primary bg-primary" /></div><span className="text-sm font-emphasis text-textSecondary">{stats.users.toLocaleString()}+ Community Members,{" "}{stats.factiiis.toLocaleString()}+ Factiiis Posted,{" "}{stats.posts.toLocaleString()}+ Posts Shared</span></div>;
  let t12;
  if ($[19] !== hero?.title) {
    t12 = hero?.title ?? <>The Internet's<br /><span className="text-primary">Truth Layer</span></>;
    $[19] = hero?.title;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <h1 className="text-3xl font-extrabold leading-tight text-textPrimary sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">{t12}</h1>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== hero?.lead) {
    t14 = hero?.lead ?? <>Discover, verify, and share facts — while keeping your privacy.<br /><span className="text-primary">Humanity first. Privacy by default. Radical transparency.</span></>;
    $[23] = hero?.lead;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <p className="mt-4 text-lg leading-relaxed text-textSecondary sm:text-xl md:text-2xl">{t14}</p>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== currentUser?.isAuthenticated || $[28] !== openSignupModal || $[29] !== router) {
    t16 = currentUser?.isAuthenticated ? () => router.push("/home") : openSignupModal;
    $[27] = currentUser?.isAuthenticated;
    $[28] = openSignupModal;
    $[29] = router;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = currentUser?.isAuthenticated ? "Go Home" : "Join the Community";
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <Button onClick={t16} variant="PRIMARY" text={t17} classNames="px-6 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 sm:px-8 sm:py-4 sm:text-lg md:px-10 md:py-5" />;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setDesktopDownloadOpen(true);
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <button type="button" onClick={t19} className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-textSecondary transition-colors hover:text-textPrimary"><Monitor className="size-4" />Download for Desktop</button>;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t18) {
    t21 = <div className="mt-8 flex items-center gap-4 sm:mt-10">{t18}{t20}</div>;
    $[36] = t18;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Apple className="size-5" />;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <a href="https://apps.apple.com/app/id6445829602" target="_blank" rel="noopener noreferrer" aria-label={"Download on the App Store \u2014 Factiii"} className="inline-flex items-center gap-2 rounded-lg border border-borderPrimary bg-background-secondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition-colors hover:bg-background-light hover:text-primary">{t22}<span className="flex flex-col leading-tight"><span className="text-[10px] font-normal text-textSecondary">Download on the</span>App Store</span></a>;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Play className="size-5" />;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="mt-6 flex flex-wrap items-center gap-3">{t23}<a href="https://play.google.com/store/apps/details?id=com.factiii" target="_blank" rel="noopener noreferrer" aria-label={"Get it on Google Play \u2014 Factiii"} className="inline-flex items-center gap-2 rounded-lg border border-borderPrimary bg-background-secondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition-colors hover:bg-background-light hover:text-primary">{t24}<span className="flex flex-col leading-tight"><span className="text-[10px] font-normal text-textSecondary">Get it on</span>Google Play</span></a></div>;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t11 || $[43] !== t13 || $[44] !== t15 || $[45] !== t21) {
    t26 = <div>{t11}{t13}{t15}{t21}{t25}</div>;
    $[42] = t11;
    $[43] = t13;
    $[44] = t15;
    $[45] = t21;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="relative"><div className="relative flex items-center justify-center bg-transparent"><div className="relative aspect-[9/19.5] w-full max-w-sm rotate-[4deg] animate-[heroFloat_5s_ease-in-out_infinite] bg-transparent motion-reduce:animate-none"><Image src="/about/home.webp" alt="Factiii Platform Demo" fill={true} priority={true} loading="eager" fetchPriority="high" sizes="(max-width: 432px) calc(100vw - 3rem), 384px" className="!bg-transparent object-contain" style={{
            backgroundColor: "transparent"
          }} /></div></div><div className="pointer-events-none absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 blur-3xl" /><div className="pointer-events-none absolute -bottom-8 -left-8 size-32 rounded-full bg-primary/20 blur-3xl" /></div>;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t26) {
    t28 = <section className="relative overflow-hidden bg-background-primary px-6 py-20 pt-24 md:py-32">{t10}<div className="relative mx-auto max-w-7xl"><div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">{t26}{t27}</div></div></section>;
    $[48] = t26;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== marketing) {
    t29 = marketing && <section className="relative bg-background-secondary py-8" aria-labelledby="partners-heading"><div className="mx-auto"><div className="mb-4 flex justify-center"><div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-primary"><Handshake className="mr-2 size-3" /><h2 id="partners-heading" className="text-xs font-semibold leading-none">From the Team Behind</h2></div></div><PartnersCarousel partners={[{
          href: "https://taptrack.io/",
          logo: <Clock className="size-8" />,
          name: "Taptrack"
        }, {
          href: "https://link3d.io/",
          logo: <LinkIcon className="size-8" />,
          name: "Link3D"
        }, {
          href: "https://greasemoto.com/",
          logo: "/images/greasemotoLogoWhite.png",
          name: "Greasemoto"
        }]} /></div></section>;
    $[50] = marketing;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><CheckCircle className="mr-2 size-4" /><span className="text-sm font-semibold">Our Principles</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl" data-animate={true}>Humanity • Privacy • Transparency</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Our north star is simple: protect people, protect their data, and make truth easy to find.</p></div>;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><Heart className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Humanity First</h3><p className="leading-relaxed text-textSecondary">Mental health over monetization. No manipulative dark patterns, no dopamine traps—use Factiii on your terms.</p></div>;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><ShieldCheck className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Privacy You Own</h3><p className="leading-relaxed text-textSecondary">You own your data. Local-first by default with explicit opt-in sharing. We never sell your data—ever.</p></div>;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <section className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl">{t30}<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{t31}{t32}<div className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><Eye className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">Radical Transparency</h3><p className="leading-relaxed text-textSecondary">Clear provenance with citations, timestamps, and locations. Explanations for why content and ads appear to you.</p></div></div></div></section>;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== currentUser || $[57] !== foundersModalOpen || $[58] !== foundersToken || $[59] !== goToNext || $[60] !== goToPrevious || $[61] !== handleManualScroll || $[62] !== leftTron || $[63] !== marketing || $[64] !== openAuthContinueModal || $[65] !== rightTron || $[66] !== router) {
    t34 = marketing && <><section id="features" className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl"><div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><CheckCircle className="mr-2 size-4" /><span className="text-sm font-semibold">Benefits</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl" data-animate={true}>Why Choose Factiii</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Everything you need to discover, verify, and share trustworthy information</p></div><div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{benefits.map(_temp5)}</div></div></section><section className="bg-background-secondary px-6 py-24"><div className="mx-auto max-w-7xl"><div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><PlayCircle className="mr-2 size-4" /><span className="text-sm font-semibold">How It Works</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl lg:text-6xl" data-animate={true}>Explore Our Features</h2><p className="mx-auto mt-6 max-w-3xl text-xl text-textSecondary">Discover the powerful features that make Factiii the ultimate platform for truth and privacy</p></div><div className="relative"><button onClick={goToPrevious} className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:scale-110 hover:bg-black/70 sm:left-0 sm:p-3" aria-label="Previous feature"><ChevronLeft className="size-6" /></button><button onClick={goToNext} className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:scale-110 hover:bg-black/70 sm:right-0 sm:p-3" aria-label="Next feature"><ChevronRight className="size-6" /></button><div ref={scrollContainerRef} className="flex gap-6 overflow-x-auto pb-4" style={{
              scrollSnapType: "x mandatory",
              scrollPadding: "0 50%",
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }} onScroll={handleManualScroll}><style>{"\n                div::-webkit-scrollbar {\n                  display: none;\n                }\n              "}</style><div className="w-1/2 shrink-0" aria-hidden="true" />{features.map(_temp6)}<div className="w-1/2 shrink-0" aria-hidden="true" /></div></div></div></section><section className="relative bg-background-secondary px-6 py-24"><div className="relative mx-auto max-w-7xl"><div className="mb-20 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary backdrop-blur-sm"><Hourglass className="mr-2 size-4" /><span className="text-sm font-semibold">What's Next</span></div><h2 className="text-4xl font-semibold text-textPrimary md:text-5xl lg:text-6xl" data-animate={true}>Coming Soon</h2><p className="mx-auto mt-6 max-w-3xl text-xl text-textSecondary">Upcoming features in our to-do list!</p></div><div className="grid gap-8 md:grid-cols-3"><div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"><div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" /><div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Advanced AI</h3><p className="leading-relaxed text-textSecondary">Message privately with AI, customize their behaviors, and build as many factiii characters as you want. Trons power every model — pick the best one for the job with no subscriptions and no lock-ins.</p></div></div><div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"><div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" /><div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Globe className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Post Once, Share Everywhere</h3><p className="leading-relaxed text-textSecondary">Publish a single post from Factiii and we send it everywhere — Reddit, YouTube, TikTok and more — through our API integrations, with new networks added as we go.</p></div></div><div className="group relative overflow-hidden rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"><div className="absolute -right-8 -top-8 size-32 rounded-full bg-primary/20 transition-all duration-500 group-hover:scale-110" /><div className="relative"><div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><LinkIcon className="size-8" /></div><h3 className="mb-4 text-2xl font-semibold text-textPrimary">Connected Workspaces</h3><p className="leading-relaxed text-textSecondary">Plug your Atlassian boards into AI to plan and ship code, and sync files straight from OneDrive. More integrations land as we build them out.</p></div></div></div></div></section><FeatureRequests /><section id="pricing" className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-7xl"><div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><Target className="mr-2 size-4" /><span className="text-sm font-semibold">Pricing</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl">Simple, Transparent Pricing</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Factiii is free to use. Trons are your AI credits — they power AI features, and you can also give them as awards on posts you love (the trons go straight to the author). Our pricing is open math: one tron is one millionth of a dollar of what the AI costs us, and $1 buys a million of them, the same for everyone. Premium members spend trons at exactly our cost; everyone else spends a small margin more. No surveillance advertising, no dark patterns, no data sales.</p></div><div className="grid gap-6 md:gap-8 lg:grid-cols-3">{leftTron && <div className="rounded-2xl bg-background-secondary p-8 shadow-lg"><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={leftTron.image} alt={leftTron.title || "Tron package"} size="md" round={true} /><h3 className="text-2xl font-semibold text-textPrimary">{leftTron.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-textPrimary">${(leftTron.displayPrice / 100).toFixed(2)}</span>{leftTron.displayPrice < leftTron.price && <span className="text-2xl text-textSecondary line-through">${(leftTron.price / 100).toFixed(2)}</span>}<span className="text-textSecondary"> one-time</span></div>{leftTron.tierPricingLabel && <div className="mb-6 inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-500">{leftTron.tierPricingLabel}</div>}<Button onClick={() => router.push("/products")} variant="SECONDARY" text="View Details" classNames="w-full mb-6" /><ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{Number(leftTron.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li>{(leftTron.tron?.premiumDays ?? 0) > 0 && <li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{leftTron.tron?.premiumDays} days premium access</span></li>}<li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Priority support</span></li></ul></div>}{foundersToken && <div className="relative rounded-2xl bg-primary p-8 shadow-2xl"><div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1 text-sm font-semibold text-indigo-600">Limited Edition</div><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={foundersToken.image} alt={foundersToken.title || "Founders Token"} size="md" /><h3 className="text-2xl font-semibold text-white">{foundersToken.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-white">${(foundersToken.displayPrice / 100).toFixed(0)}</span>{foundersToken.displayPrice < foundersToken.price && <span className="text-2xl text-white/60 line-through">${(foundersToken.price / 100).toFixed(0)}</span>}<span className="text-white/80"> one-time</span></div>{foundersToken.displayPrice < foundersToken.price && <div className="mb-2 inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-bold text-indigo-600">{Math.round((1 - foundersToken.displayPrice / foundersToken.price) * 100)}% off — founding wave pricing</div>}<p className="mb-6 text-sm text-white/80">3,000 available at this price — only 10,000 Founders Tokens will ever exist. The price rises when this wave sells out.</p><Button onClick={() => {
                if (!currentUser.isAuthenticated) {
                  openAuthContinueModal();
                  return;
                }
                if (foundersToken.owned) {
                  router.push("/products");
                  return;
                }
                setFoundersModalOpen(true);
              }} variant="SECONDARY" text="Secure Yours" classNames="w-full hover:bg-primary mb-6 border-2 border-white shadow-lg font-semibold" />{foundersModalOpen && <PurchaseModal product={foundersToken} closeHandler={() => setFoundersModalOpen(false)} />}<ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">{Number(foundersToken.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Lifetime premium access (100-year subscription)</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Exclusive Founders space</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Spend every tron at exact AI cost, forever</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-white" /><span className="text-white">Only {foundersToken.inventory.toLocaleString()} left at this price</span></li></ul></div>}{rightTron && <div className="rounded-2xl bg-background-secondary p-8 shadow-lg"><div className="mb-2 flex items-center gap-3"><ProductImage imageKey={rightTron.image} alt={rightTron.title || "Tron package"} size="md" round={true} /><h3 className="text-2xl font-semibold text-textPrimary">{rightTron.title}</h3></div><div className="mb-2 flex flex-wrap items-baseline gap-x-3"><span className="text-5xl font-semibold text-textPrimary">${(rightTron.displayPrice / 100).toFixed(2)}</span>{rightTron.displayPrice < rightTron.price && <span className="text-2xl text-textSecondary line-through">${(rightTron.price / 100).toFixed(2)}</span>}<span className="text-textSecondary"> one-time</span></div>{rightTron.tierPricingLabel && <div className="mb-6 inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-500">{rightTron.tierPricingLabel}</div>}<Button onClick={() => router.push("/products")} variant="SECONDARY" text="View Details" classNames="w-full mb-6" /><ul className="space-y-4"><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{Number(rightTron.tron?.quantity || 0).toLocaleString()}{" "}trons</span></li>{(rightTron.tron?.premiumDays ?? 0) > 0 && <li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">{rightTron.tron?.premiumDays} days premium access</span></li>}<li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Trons power AI features</span></li><li className="flex items-start"><CheckCircle className="mr-3 mt-1 size-5 shrink-0 text-primary" /><span className="text-textSecondary">Priority support</span></li></ul></div>}</div></div></section></>;
    $[56] = currentUser;
    $[57] = foundersModalOpen;
    $[58] = foundersToken;
    $[59] = goToNext;
    $[60] = goToPrevious;
    $[61] = handleManualScroll;
    $[62] = leftTron;
    $[63] = marketing;
    $[64] = openAuthContinueModal;
    $[65] = rightTron;
    $[66] = router;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><Sparkles className="mr-2 size-4" /><span className="text-sm font-semibold">Our Vision</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl" data-animate={true}>Why We're Building Factiii</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">The principles that drive every decision we make</p></div>;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <section className="bg-background-secondary px-6 py-24"><div className="mx-auto max-w-7xl">{t35}<div className="grid gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">{teamVisions.map(_temp7)}</div></div></section>;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== marketing || $[71] !== openFaqId) {
    t37 = marketing && <section className="bg-background-primary px-6 py-24"><div className="mx-auto max-w-4xl"><div className="mb-16 text-center"><div className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-primary"><FileText className="mr-2 size-4" /><span className="text-sm font-semibold">FAQ</span></div><h2 className="text-3xl font-semibold text-textPrimary sm:text-4xl md:text-5xl">Frequently Asked Questions</h2><p className="mx-auto mt-4 max-w-2xl text-xl text-textSecondary">Get answers to common questions about Factiii</p></div><div className="space-y-4">{faqs.map(faq => <div key={faq.id} className="overflow-hidden rounded-xl bg-background-secondary shadow-lg"><button onClick={() => setOpenFaqId(openFaqId === faq.id ? null : faq.id)} aria-expanded={openFaqId === faq.id} aria-controls={`faq-answer-${faq.id}`} className="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-background-light"><h3 className="text-lg font-semibold text-textPrimary">{faq.question}</h3><ChevronDown className={`size-5 shrink-0 text-primary transition-transform duration-300 ${openFaqId === faq.id ? "rotate-180" : ""}`} /></button><div id={`faq-answer-${faq.id}`} role="region" className={`grid transition-[grid-template-rows,opacity] duration-300 ${openFaqId === faq.id ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}><div className="overflow-hidden"><div className="px-6 pb-6"><p className="leading-relaxed text-textSecondary">{faq.answer}</p></div></div></div></div>)}</div></div></section>;
    $[70] = marketing;
    $[71] = openFaqId;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  const t38 = "contact";
  const t39 = "relative bg-primary px-6 py-32";
  const t40 = "relative mx-auto max-w-5xl text-center";
  let t41;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <h2 className="text-4xl font-semibold text-white md:text-5xl lg:text-6xl">Ready to Join the Future of Truth?</h2>;
    $[73] = t41;
  } else {
    t41 = $[73];
  }
  const t42 = "mx-auto mt-6 max-w-2xl text-xl text-white/90";
  const t43 = "Join ";
  const t44 = stats.users.toLocaleString();
  let t45;
  if ($[74] !== t44) {
    t45 = <p className={t42}>{t43}{t44}+ community members already using Factiii to discover, verify, and share trustworthy information.</p>;
    $[74] = t44;
    $[75] = t45;
  } else {
    t45 = $[75];
  }
  let t46;
  if ($[76] !== currentUser?.isAuthenticated || $[77] !== openSignupModal || $[78] !== router) {
    t46 = currentUser?.isAuthenticated ? () => router.push("/home") : openSignupModal;
    $[76] = currentUser?.isAuthenticated;
    $[77] = openSignupModal;
    $[78] = router;
    $[79] = t46;
  } else {
    t46 = $[79];
  }
  const t47 = currentUser?.isAuthenticated ? "Go to App" : "Join the Community";
  let t48;
  if ($[80] !== t46 || $[81] !== t47) {
    t48 = <Button onClick={t46} variant="PRIMARY" text={t47} classNames="px-12 py-6 text-lg font-semibold shadow-2xl transform transition-all duration-300 w-72 border-2 border-white hover:!bg-white hover:!text-primary hover:scale-105" />;
    $[80] = t46;
    $[81] = t47;
    $[82] = t48;
  } else {
    t48 = $[82];
  }
  let t49;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Button onClick={() => setShowContactModal(true)} variant="SECONDARY" text="Contact Us" classNames="border-2 border-white/50 bg-white/10 px-12 py-6 text-lg font-semibold text-white backdrop-blur-sm hover:bg-white hover:text-primary transform hover:scale-105 transition-all duration-300 w-72" />;
    $[83] = t49;
  } else {
    t49 = $[83];
  }
  let t50;
  if ($[84] !== t48) {
    t50 = <div className="mt-12 flex flex-col items-center justify-center gap-6 sm:flex-row">{t48}{t49}</div>;
    $[84] = t48;
    $[85] = t50;
  } else {
    t50 = $[85];
  }
  let t51;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <p className="mt-8 text-sm text-white/80">Join the community • Share your truth • Connect with others</p>;
    $[86] = t51;
  } else {
    t51 = $[86];
  }
  let t52;
  if ($[87] !== t41 || $[88] !== t45 || $[89] !== t50) {
    t52 = <section id={t38} className={t39}><div className={t40}><div>{t41}{t45}{t50}{t51}</div></div></section>;
    $[87] = t41;
    $[88] = t45;
    $[89] = t50;
    $[90] = t52;
  } else {
    t52 = $[90];
  }
  let t53;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <MarketingFooter />;
    $[91] = t53;
  } else {
    t53 = $[91];
  }
  let t54;
  if ($[92] !== showContactModal) {
    t54 = showContactModal && <ContactFormModal onClose={() => setShowContactModal(false)} />;
    $[92] = showContactModal;
    $[93] = t54;
  } else {
    t54 = $[93];
  }
  let t55;
  if ($[94] !== desktopDownloadOpen) {
    t55 = desktopDownloadOpen && <DesktopDownloadModal closeHandler={() => setDesktopDownloadOpen(false)} />;
    $[94] = desktopDownloadOpen;
    $[95] = t55;
  } else {
    t55 = $[95];
  }
  let t56;
  if ($[96] !== companySlot || $[97] !== exploreSlot || $[98] !== t28 || $[99] !== t29 || $[100] !== t33 || $[101] !== t34 || $[102] !== t36 || $[103] !== t37 || $[104] !== t52 || $[105] !== t54 || $[106] !== t55 || $[107] !== t8 || $[108] !== t9) {
    t56 = <>{t8}{t9}{t28}{companySlot}{t29}{t33}{t34}{t36}{t37}{t52}{exploreSlot}{t53}{t54}{t55}</>;
    $[96] = companySlot;
    $[97] = exploreSlot;
    $[98] = t28;
    $[99] = t29;
    $[100] = t33;
    $[101] = t34;
    $[102] = t36;
    $[103] = t37;
    $[104] = t52;
    $[105] = t54;
    $[106] = t55;
    $[107] = t8;
    $[108] = t9;
    $[109] = t56;
  } else {
    t56 = $[109];
  }
  return t56;
};
function _temp(product) {
  return product.type === "TRON";
}
function _temp2(product_0) {
  return product_0.type === "FOUNDERS_TOKEN";
}
function _temp3(product_1) {
  return product_1.purchasable;
}
function _temp4(a, b) {
  return a.price - b.price;
}
function _temp5(benefit) {
  return <div key={benefit.id} className="group rounded-2xl bg-background-secondary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><benefit.icon className="size-7" /></div><h3 className="mb-3 text-xl font-semibold text-textPrimary">{benefit.title}</h3><p className="leading-relaxed text-textSecondary">{benefit.description}</p></div>;
}
function _temp6(feature) {
  return <div key={feature.id} data-feature-card="true" className="relative min-w-[90%] sm:min-w-[400px] md:min-w-[450px]" style={{
    scrollSnapAlign: "center"
  }}><div className="overflow-hidden rounded-2xl bg-background-secondary shadow-lg transition-all duration-300 hover:shadow-xl"><FeatureImage src={feature.videoSrc} alt={feature.title} /><div className="p-6"><div className="mb-3 flex items-center space-x-3"><div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><feature.icon className="size-5" /></div><h3 className="text-xl font-semibold text-textPrimary">{feature.title}</h3></div><p className="text-sm leading-relaxed text-textSecondary">{feature.description}</p></div></div></div>;
}
function _temp7(vision) {
  return <div key={vision.id} className="group rounded-2xl bg-background-primary p-8 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"><div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-white"><vision.icon className="size-7" /></div><p className="mb-6 text-lg leading-relaxed text-textSecondary">"{vision.content}"</p><div className="flex items-center"><div className="ml-0"><p className="font-semibold text-textPrimary">{vision.name}</p><p className="text-sm text-primary">{vision.role}</p></div></div></div>;
}